<script setup lang="ts">
import { Roles } from '@/constants'
import type { UserModel } from '@/services/http/httpResponses'
import usersHttpService from '@/services/http/usersHttpService'
import { computed, ref } from 'vue'
import { z } from 'zod'
import { zodResolver } from '@primevue/forms/resolvers/zod'
import { useToast } from 'primevue/usetoast'
import type { Id } from '@raildev/shared-utils'
import jwtService from '@/services/jwtService'
import { jwtDecode } from 'jwt-decode'
import type { JwtPayload } from '@/types/auth'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.CareerGuidanceSpecialist],
  },
})

const toast = useToast()

const token = jwtService.getToken()
const currentRole = token ? (jwtDecode<JwtPayload>(token).role as Roles) : null
const isAdmin = computed(() => currentRole === Roles.Admin)

const filterParams = {
  role: null as Roles | null,
  name: null as string | null,
  email: null as string | null,
  phoneNumber: null as string | null,
}

const users = ref<UserModel[]>([])

const roleOptions = ref<{ label: string; value: Roles }[]>([
  { label: 'Админ', value: 'Admin' },
  { label: 'Профориентолог', value: 'CareerGuidanceSpecialist' },
  { label: 'Пользователь', value: 'User' },
  { label: 'Агент', value: 'Agent' },
])

const refreshUsers = async () => {
  const usersResponse = await usersHttpService.getUsers(filterParams as { role: Roles | null })
  users.value = usersResponse.users
}

const formattedUsers = computed(() => {
  return users.value.map((user) => ({
    id: user.id,
    name: user.name ?? '-',
    email: user.email ?? '-',
    role: user.role ?? '-',
    phoneNumber: user.phoneNumber ?? '-',
  }))
})

const canManageUser = (row: { role: string }) => {
  return row.role !== Roles.User && row.role !== '-'
}

const passwordRules = z
  .string()
  .min(6, { message: 'Пароль должен содержать минимум 6 символов' })
  .refine((value) => /[a-z]/.test(value), {
    message: 'Пароль должен содержать хотя бы одну строчную букву',
  })
  .refine((value) => /[A-Z]/.test(value), {
    message: 'Пароль должен содержать хотя бы одну заглавную букву',
  })
  .refine((value) => /\d+/.test(value), {
    message: 'Пароль должен содержать хотя бы одну цифру',
  })
  .refine((value) => /\W/.test(value), {
    message: 'Пароль должен содержать хотя бы один специальный символ',
  })

const createUserSchema = z
  .object({
    email: z.email({ message: 'Введите корректный email' }),
    name: z.string().optional(),
    role: z.enum([Roles.Admin, Roles.CareerGuidanceSpecialist, Roles.Agent], {
      message: 'Выберите роль',
    }),
    password: passwordRules,
    confirmPassword: z.string().min(1, { message: 'Повторите пароль' }),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Пароли не совпадают',
    path: ['confirmPassword'],
  })

const createUserResolver = zodResolver(createUserSchema)
const showCreateDialog = ref(false)

const createInitialValues: Record<
  'email' | 'name' | 'role' | 'password' | 'confirmPassword',
  string
> = {
  email: '',
  name: '',
  role: Roles.Agent,
  password: '',
  confirmPassword: '',
}

const createRoleOptions = computed(() => {
  const all = [
    { label: 'Админ', value: Roles.Admin },
    { label: 'Профориентолог', value: Roles.CareerGuidanceSpecialist },
    { label: 'Агент', value: Roles.Agent },
  ]

  return isAdmin.value ? all : all.filter((x) => x.value !== Roles.Admin)
})

const openCreateDialog = () => {
  showCreateDialog.value = true
}

const createUser = async ({
  valid,
  values,
}: {
  valid: boolean
  values: z.infer<typeof createUserSchema>
}) => {
  if (!valid) {
    return
  }

  try {
    await usersHttpService.createUser(
      values.email,
      values.password,
      values.role as Roles,
      (values.name ?? '').trim() || null,
    )

    toast.add({
      severity: 'success',
      summary: 'Успех',
      detail: 'Пользователь создан',
      life: 3000,
    })

    showCreateDialog.value = false
    await refreshUsers()
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось создать пользователя. Возможно, email уже занят.',
      life: 3000,
    })
  }
}

const showEditDialog = ref(false)
const selectedUser = ref<UserModel | null>(null)

const editSchema = z.object({
  email: z.email({ message: 'Введите корректный email' }),
  name: z.string().nullable().optional(),
})
const editResolver = zodResolver(editSchema)

const editInitialValues = computed(() => ({
  email: selectedUser.value?.email ?? '',
  name: selectedUser.value?.name ?? '',
}))

const openEditDialog = (row: { id: Id }) => {
  const u = users.value.find((x) => x.id === row.id) ?? null
  selectedUser.value = u
  showEditDialog.value = true
}

const saveEdit = async ({
  valid,
  values,
}: {
  valid: boolean
  values: { email: string; name?: string | null }
}) => {
  if (!valid || selectedUser.value == null) {
    return
  }

  try {
    await usersHttpService.updateUserDetails(
      selectedUser.value.id,
      values.email,
      (values.name ?? '')?.trim() || null,
    )

    toast.add({
      severity: 'success',
      summary: 'Успех',
      detail: 'Данные обновлены',
      life: 3000,
    })

    showEditDialog.value = false
    await refreshUsers()
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось обновить данные. Возможно, email уже занят.',
      life: 3000,
    })
  }
}

const showPasswordDialog = ref(false)

const passwordSchema = z
  .object({
    password: passwordRules,
    confirmPassword: z.string().min(1, { message: 'Повторите пароль' }),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Пароли не совпадают',
    path: ['confirmPassword'],
  })

const passwordResolver = zodResolver(passwordSchema)

const passwordInitialValues = computed(() => ({
  password: '',
  confirmPassword: '',
}))

const openPasswordDialog = (row: { id: Id }) => {
  const u = users.value.find((x) => x.id === row.id) ?? null
  selectedUser.value = u
  showPasswordDialog.value = true
}

const savePassword = async ({
  valid,
  values,
}: {
  valid: boolean
  values: { password: string; confirmPassword: string }
}) => {
  if (!valid || selectedUser.value == null) {
    return
  }

  try {
    await usersHttpService.updateUserPassword(selectedUser.value.id, values.password)

    toast.add({
      severity: 'success',
      summary: 'Успех',
      detail: 'Пароль обновлён',
      life: 3000,
    })

    showPasswordDialog.value = false
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось обновить пароль',
      life: 3000,
    })
  }
}

const showRoleDialog = ref(false)

const roleSchema = z.object({
  role: z.enum([Roles.Admin, Roles.CareerGuidanceSpecialist, Roles.Agent, Roles.User], {
    message: 'Выберите роль',
  }),
})
const roleResolver = zodResolver(roleSchema)

const roleInitialValues = computed(() => ({
  role: (selectedUser.value?.role ?? Roles.User) as Roles,
}))

const openRoleDialog = (row: { id: Id }) => {
  const u = users.value.find((x) => x.id === row.id) ?? null
  selectedUser.value = u
  showRoleDialog.value = true
}

const saveRole = async ({ valid, values }: { valid: boolean; values: { role: Roles } }) => {
  if (!valid || selectedUser.value == null) {
    return
  }

  try {
    await usersHttpService.updateUserRole(selectedUser.value.id, values.role)

    toast.add({
      severity: 'success',
      summary: 'Успех',
      detail: 'Роль обновлена',
      life: 3000,
    })

    showRoleDialog.value = false
    await refreshUsers()
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось обновить роль',
      life: 3000,
    })
  }
}

await refreshUsers()
</script>
<template>
  <section class="flex flex-col w-full">
    <Toast />
    <div class="flex gap-4 mb-4 justify-between items-end sm:items-center">
      <div class="grow flex gap-3 flex-col items-start sm:flex-row sm:items-center">
        <p>Фильтровать по</p>

        <FloatLabel class="max-w-56 w-full" variant="on">
          <Select
            class="max-w-56 w-full"
            inputId="role-select"
            name="role"
            :options="roleOptions"
            option-label="label"
            option-value="value"
            v-model="filterParams.role"
            show-clear
          />
          <label for="role-select"> Роль </label>
        </FloatLabel>
        <FloatLabel class="max-w-56 w-full" variant="on">
          <InputText
            class="max-w-56 w-full"
            id="name-input"
            name="name"
            v-model="filterParams.name"
          />
          <label for="name-input"> Имя </label>
        </FloatLabel>
        <FloatLabel class="max-w-56 w-full" variant="on">
          <InputText
            class="max-w-56 w-full"
            id="email-input"
            name="email"
            v-model="filterParams.email"
          />
          <label for="email-input"> Почта </label>
        </FloatLabel>
        <FloatLabel class="max-w-56 w-full" variant="on">
          <InputText
            class="max-w-56 w-full"
            id="phone-input"
            name="phone"
            v-model="filterParams.phoneNumber"
          />
          <label for="phone-input"> Телефон </label>
        </FloatLabel>
      </div>

      <div class="flex gap-2">
        <Button label="Применить" @click="refreshUsers" />
        <Button label="Добавить" @click="openCreateDialog" />
      </div>
    </div>

    <DataTable
      :value="formattedUsers"
      dataKey="id"
      :pt="{ emptyMessageCell: users.length < 1 ? 'border-none py-12' : '' }"
    >
      <Column field="name" header="Имя" />
      <Column field="email" header="Почта" />
      <Column field="role" header="Роль" />
      <Column field="phoneNumber" header="Телефон" />
      <Column header="Действия" style="min-width: 20rem">
        <template #body="slotProps">
          <div class="flex gap-2" v-if="canManageUser(slotProps.data)">
            <Button label="Редактировать" @click="openEditDialog(slotProps.data)" />
            <Button
              label="Пароль"
              severity="secondary"
              @click="openPasswordDialog(slotProps.data)"
            />
            <Button
              v-if="isAdmin"
              label="Роль"
              severity="secondary"
              @click="openRoleDialog(slotProps.data)"
            />
          </div>
        </template>
      </Column>
      <template #empty>
        <div class="flex flex-col gap-4 items-center">
          <span class="pi pi-search text-6xl" />
          <h3 class="font-medium">По выбранным фильтрам данные не найдены</h3>
        </div>
      </template>
    </DataTable>
    <Dialog
      v-model:visible="showCreateDialog"
      header="Создать пользователя"
      :modal="true"
      :class="'w-[90vw] sm:w-[600px]'"
      :closable="true"
    >
      <Form
        v-slot="$form"
        :resolver="createUserResolver"
        :initialValues="createInitialValues"
        @submit="createUser"
        class="flex flex-col gap-4"
      >
        <div class="flex flex-col gap-1">
          <label for="createEmail">Email</label>
          <InputText id="createEmail" name="email" type="email" class="w-full" />
          <Message v-if="$form.email?.invalid" severity="error" size="small" variant="simple">
            {{ $form.email?.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="createName">Имя (опционально)</label>
          <InputText id="createName" name="name" class="w-full" />
          <Message v-if="$form.name?.invalid" severity="error" size="small" variant="simple">
            {{ $form.name?.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="createRole">Роль</label>
          <Select
            id="createRole"
            name="role"
            class="w-full"
            :options="createRoleOptions"
            option-label="label"
            option-value="value"
          />
          <Message v-if="$form.role?.invalid" severity="error" size="small" variant="simple">
            {{ $form.role?.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="createPassword">Пароль</label>
          <Password
            id="createPassword"
            name="password"
            class="w-full"
            :feedback="true"
            fluid
            toggle-mask
          />
          <Message v-if="$form.password?.invalid" severity="error" size="small" variant="simple">
            {{ $form.password?.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="createConfirmPassword">Повторите пароль</label>
          <Password
            id="createConfirmPassword"
            name="confirmPassword"
            class="w-full"
            :feedback="false"
            fluid
            toggle-mask
          />
          <Message
            v-if="$form.confirmPassword?.invalid"
            severity="error"
            size="small"
            variant="simple"
          >
            {{ $form.confirmPassword?.error?.message }}
          </Message>
        </div>
        <div class="flex gap-2">
          <Button class="w-full" label="Отмена" text @click="showCreateDialog = false" />
          <Button class="w-full" type="submit" label="Создать" />
        </div>
      </Form>
    </Dialog>
    <Dialog
      v-model:visible="showEditDialog"
      header="Редактировать пользователя"
      :modal="true"
      :class="'w-[90vw] sm:w-[600px]'"
      :closable="true"
    >
      <Form
        v-slot="$form"
        :resolver="editResolver"
        :initialValues="editInitialValues"
        @submit="saveEdit"
        class="flex flex-col gap-4"
      >
        <div class="flex flex-col gap-1">
          <label for="editEmail">Email</label>
          <InputText id="editEmail" name="email" type="email" class="w-full" />
          <Message v-if="$form.email?.invalid" severity="error" size="small" variant="simple">
            {{ $form.email?.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="editName">Имя</label>
          <InputText id="editName" name="name" class="w-full" />
          <Message v-if="$form.name?.invalid" severity="error" size="small" variant="simple">
            {{ $form.name?.error?.message }}
          </Message>
        </div>
        <div class="flex gap-2">
          <Button class="w-full" label="Отмена" text @click="showEditDialog = false" />
          <Button class="w-full" type="submit" label="Сохранить" />
        </div>
      </Form>
    </Dialog>
    <Dialog
      v-model:visible="showPasswordDialog"
      header="Сменить пароль"
      :modal="true"
      :class="'w-[90vw] sm:w-[600px]'"
      :closable="true"
    >
      <Form
        v-slot="$form"
        :resolver="passwordResolver"
        :initialValues="passwordInitialValues"
        @submit="savePassword"
        class="flex flex-col gap-4"
      >
        <div class="flex flex-col gap-1">
          <label for="newPassword">Новый пароль</label>
          <Password
            id="newPassword"
            name="password"
            class="w-full"
            :feedback="true"
            fluid
            toggle-mask
          />
          <Message v-if="$form.password?.invalid" severity="error" size="small" variant="simple">
            {{ $form.password?.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="confirmNewPassword">Повторите пароль</label>
          <Password
            id="confirmNewPassword"
            name="confirmPassword"
            class="w-full"
            :feedback="false"
            fluid
            toggle-mask
          />
          <Message
            v-if="$form.confirmPassword?.invalid"
            severity="error"
            size="small"
            variant="simple"
          >
            {{ $form.confirmPassword?.error?.message }}
          </Message>
        </div>
        <div class="flex gap-2">
          <Button class="w-full" label="Отмена" text @click="showPasswordDialog = false" />
          <Button class="w-full" type="submit" label="Сохранить" />
        </div>
      </Form>
    </Dialog>
    <Dialog
      v-model:visible="showRoleDialog"
      header="Изменить роль"
      :modal="true"
      :class="'w-[90vw] sm:w-[600px]'"
      :closable="true"
    >
      <Form
        v-slot="$form"
        :resolver="roleResolver"
        :initialValues="roleInitialValues"
        @submit="saveRole"
        class="flex flex-col gap-4"
      >
        <div class="flex flex-col gap-1">
          <label for="roleSelect">Роль</label>
          <Select
            id="roleSelect"
            name="role"
            class="w-full"
            :options="roleOptions"
            option-label="label"
            option-value="value"
          />
          <Message v-if="$form.role?.invalid" severity="error" size="small" variant="simple">
            {{ $form.role?.error?.message }}
          </Message>
        </div>
        <div class="flex gap-2">
          <Button class="w-full" label="Отмена" text @click="showRoleDialog = false" />
          <Button class="w-full" type="submit" label="Сохранить" />
        </div>
      </Form>
    </Dialog>
  </section>
</template>
<style scoped></style>
